import type { FindFn } from "@shared/models/analytics/base"
import type { EditorOperationEvent } from "@shared/unified-tiptap/analytics/operation-event"
import { partial } from "lodash-es"
import type { PreparedAction } from "../../action-doc-builder"
import type { Narrative } from "../create-narrative"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"
import { lessonAnalyticsCore } from "./lesson-analytics-core"

interface Payload {
  cardStackId: string
  planbookId: string
  courseId: string | null
  dateString: string | null
  event: EditorOperationEvent
}

/**
 * The analytics-only action one editor operation dispatches. It carries no patches: the edit itself
 * persists through the lesson's own actions, and this reports the fact the editor stated at the
 * moment it happened rather than when that edit is committed.
 */
export function prepareEditorOperation(payload: Payload): PreparedAction {
  return {
    name: "LESSON_EDITOR_OPERATION",
    payload: payload,
    patches: null,
    undoPatches: null,
    narrative: partial(narrative, payload),
  }
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
  const { event } = payload
  const models = defaultLessonModels(payload, findFn, userId)
  const core = lessonAnalyticsCore(payload.cardStackId, payload.planbookId, models, findFn, userId)

  return {
    titleForAnalytics: "Editor - Operation",
    context: {
      ...defaultLessonContext(payload, models),
      editorOperation: event.operation,
      editorSurface: event.surface,
      editorArgs: event.args,
      interactionId: event.interactionId,
      occurredAt: event.occurredAt,
    },
    newAnalyticsEvents: event.projectOperationAnalytics(core),
    isQuiet: true,
  }
}
